import { faTriangleExclamation as farTriangleExclamation } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faEnvelope as fasEnvelope, faUser as fasUser } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { EventComponentType, GA4EventParameters } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, FormCheck, FormEmail, FormInput, LazyLoad, ProgressButton, PromisePool } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Form } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import { InView } from "react-intersection-observer";
import type { Variant } from "../Menu/type";
import "./style.scss";

type Type = "sm";

const SUBCRIBERS_COUNT = "160 000";

const userNameValid = /[^#&<>"~;,!$^%{}?^0-9]$/;

export type SubscriptionTypes = "RETAIL_NEWSLETTER" | "CORPORATE_NEWSLETTER" | "CROSS_SELL";

interface Props extends MeasureComponentProps {
	size?: Type;
	subscriptionTypes?: SubscriptionTypes[];
	tag?: string;
	mainTextBlock?: ReactNode;
	buttonText?: string;
	onSubscribe?: (email: string, subscriptionTypes: SubscriptionTypes[]) => void;
	variant?: Variant;
	theme?: "dark" | "light";
	title?: string;
}

interface State extends MeasureComponentState {
	buttonState: ProgressButtonState;
	checkboxIsValid: boolean;
}

interface FormFields {
	email: string;
	firstName: string;
	acceptDataHandling: boolean;
}

/**
 * @Shortcode
 *
 * @title "Hírlevél feliratkozó"
 * @shortcode "newsletter_subscribe"
 * @type "widget"
 * @propsList "[{"size":"sm"},{"size":"sm","variant":"corporate"},{"variant":"retail","size":"sm"},{"variant":"corporate"},{"theme":"light"},{"variant":"corporate","theme":"light"}]"
 * @lazyHydrate "whenIdle"
 * @importMode "eager"
 */
export class NewsletterSubscribe extends MeasureComponent<Props, State> {
	private readonly promisePool = new PromisePool();
	protected productTypes = "";
	protected readonly componentType: EventComponentType = "widget";

	public static defaultProps = { title: "Iratkozz fel hírlevelünkre" };

	protected getInitialState(): State {
		return { ...super.getInitialState(), buttonState: undefined, checkboxIsValid: true };
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	private getSubscriptionTypes(): SubscriptionTypes[] {
		const { subscriptionTypes, variant } = this.props;

		if (subscriptionTypes?.length) {
			return subscriptionTypes;
		}

		return variant === "corporate" ? ["CORPORATE_NEWSLETTER"] : ["RETAIL_NEWSLETTER"];
	}

	public render(): ReactNode {
		const { mainTextBlock, theme, title } = this.props;
		const { wasVisible, buttonState } = this.state;
		const subscriptionTypes = this.getSubscriptionTypes();
		const defaultSubscibeTextBlock: ReactNode = subscriptionTypes.includes("CORPORATE_NEWSLETTER") ? (
			<>
				<strong>Iratkozz fel</strong> vállalati hírlevelünkre, hogy elsők között{" "}
				<strong>értesülj vállalkozásod új pénzügyi lehetőségeiről</strong>, valamint az azt érinthető{" "}
				<strong>fontos gazdasági hírekről.</strong>
			</>
		) : (
			<>
				Csatlakozz te is a több mint <strong className="text-nowrap">{SUBCRIBERS_COUNT} feliratkozott</strong>{" "}
				olvasónkhoz és értesülj elsők között a <strong>gazdasági hírek</strong>ről és a{" "}
				<strong>legjobb pénzügyi ajánlatok</strong>ról.
			</>
		);

		const themeTextClasses = theme === "light" ? "text-white" : undefined;

		if (buttonState === "done") {
			return (
				<div className={themeTextClasses}>
					<div className="display-5 font-weight-bold">
						<FontAwesomeIcon icon={fasEnvelope} className="mr-2" />
						Köszönjük, hogy feliratkoztál hírlevelünkre!
					</div>
				</div>
			);
		}

		return (
			<InView
				as="div"
				className={themeTextClasses}
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible}
				onChange={this.onVisibilityChange}
			>
				<div className="display-5 font-weight-bold mb-3">
					<FontAwesomeIcon icon={fasEnvelope} className="mr-2" />
					{title}
				</div>

				<p className="h5 font-weight-normal mb-4">{mainTextBlock || defaultSubscibeTextBlock}</p>

				{this.renderForm()}
			</InView>
		);
	}

	private renderForm(): ReactNode {
		const { size, theme } = this.props;
		let inputClassName: string;
		const themeTextClasses = theme === "light" ? "text-light" : "text-muted";

		if (size === "sm") {
			inputClassName = "mb-2 pb-0";
		} else {
			inputClassName = "mb-lg-0 pb-0";
		}

		return (
			<LazyLoad>
				<FinalForm<FormFields> onSubmit={this.onSubmit}>
					{({ handleSubmit, form }) => (
						<Form onSubmit={handleSubmit} noValidate>
							<div className="d-flex flex-wrap">
								<div className={`newsletter-input-container${size === "sm" ? "-sm" : ""}`}>
									<FormInput
										placeholder="Keresztnév"
										name="firstName"
										prepend={<FontAwesomeIcon icon={fasUser} />}
										validate={this.validFirstName}
										className={inputClassName}
										required
									/>
								</div>
								<div className={`newsletter-input-container${size === "sm" ? "-sm" : ""}`}>
									<FormEmail
										name="email"
										className={inputClassName}
										placeholder="E-mail cím"
										required
									/>
								</div>
								<div
									className={`small accept-checkbox ${
										size === "sm" ? "" : "d-md-none size-regular"
									} ${themeTextClasses}`}
								>
									{this.renderCheckbox()}
								</div>
								{this.renderButton(form)}
							</div>
							{size !== "sm" && (
								<div
									className={`small accept-checkbox ${
										size === "sm" ? "" : "size-regular"
									} d-none d-md-block mt-lg-4 ${themeTextClasses}`}
								>
									{this.renderCheckbox()}
								</div>
							)}
						</Form>
					)}
				</FinalForm>
			</LazyLoad>
		);
	}

	private renderCheckbox(): ReactNode {
		const { theme } = this.props;
		const { checkboxIsValid } = this.state;
		const themeButtonColor = theme === "light" ? "gold" : "primary";

		return (
			<>
				<FormCheck
					label={
						<div>
							Az{" "}
							<a
								className={`text-${themeButtonColor}`}
								href={Config.getBankmonitorUrl("/adatvedelem/")}
								// biome-ignore lint/a11y/noBlankTarget: in-page link
								target="_blank"
							>
								adatkezelési tájékoztatóban
							</a>{" "}
							foglaltakat elfogadom.
						</div>
					}
					name="acceptDataHandling"
					required
				/>
				{!checkboxIsValid && (
					<div className={`${theme === "light" ? "text-white" : ""} mb-3`}>
						<FontAwesomeIcon icon={farTriangleExclamation} className="mr-2" />
						Kötelező mező
					</div>
				)}
			</>
		);
	}

	private renderButton(form: FormApi<FormFields>): ReactNode {
		const { size, buttonText, theme } = this.props;
		const { buttonState } = this.state;
		const themeButtonColor = theme === "light" ? "gold" : "primary";

		if (size === "sm") {
			return (
				<div className="w-100 mt-3">
					<ProgressButton
						onClick={() => this.onButtonClick(form)}
						className="py-2"
						state={buttonState}
						variant={themeButtonColor}
						type="submit"
						block
					>
						{buttonText || "Feliratkozom"}
					</ProgressButton>
				</div>
			);
		}

		return (
			<>
				<div className="d-none d-md-block">
					<ProgressButton
						onClick={() => this.onButtonClick(form)}
						className="py-2"
						state={buttonState}
						variant={themeButtonColor}
						type="submit"
						block={size === "sm"}
					>
						{buttonText || "Feliratkozom"}
					</ProgressButton>
				</div>
				<div className="d-md-none w-100 mt-3">
					<ProgressButton
						onClick={() => this.onButtonClick(form)}
						className="py-2"
						state={buttonState}
						variant={themeButtonColor}
						type="submit"
						block
					>
						{buttonText || "Feliratkozom"}
					</ProgressButton>
				</div>
			</>
		);
	}

	private onButtonClick(form: FormApi<FormFields>): void {
		this.setState({
			checkboxIsValid: form.getFieldState("acceptDataHandling")?.value,
		});
	}

	@autobind
	private onSubmit(values: FormFields): void {
		const { onSubscribe = undefined, tag = undefined } = this.props;
		const subscriptionTypes = this.getSubscriptionTypes();
		this.setState({
			buttonState: "loading",
		});

		// Feliratkozás
		this.promisePool
			.add(
				http.post(Config.getWpRestApiUrl(`/subscribe/${encodeURIComponent(values.email)}`), {
					subscriptionTypes,
					firstName: values.firstName,
					tag,
				}),
			)
			.then(() => {
				if (onSubscribe) {
					onSubscribe(values.email, subscriptionTypes);
				}

				this.setState({
					buttonState: "done",
				});

				this.sendEvent("BM_newsletterSubscription", {
					buttonId: GoogleAnalytics4.generateId("bi"),
					email: values.email,
					firstName: values.firstName,
					newsletterType: !subscriptionTypes.length ? "general" : subscriptionTypes.join(","),
					user_data: {
						email_address: values.email,
						address: {
							first_name: values.firstName,
						},
					},
				});
			})
			.catch((error: Error) => {
				this.setState({
					buttonState: "error",
				});

				this.promisePool.addTimeout().then(() => {
					this.setState({
						buttonState: undefined,
					});
				});

				logError(error);
			});
	}

	private validFirstName(givenName: string): string {
		const firstName = givenName?.trim();

		if (!firstName || !firstName?.length) {
			return "A keresztnév megadása kötelező!";
		}

		if (firstName.length < 3) {
			return "A keresztnévnek legalább 3 karakterből kell állnia!";
		}

		if (firstName.length > 32) {
			return "A keresztnévnek legfeljebb 32 karakterből állhat!";
		}

		if (!userNameValid.test(firstName)) {
			return "Érvénytelen karakter a névben!";
		}
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		const subscriptionTypes = this.getSubscriptionTypes();

		return {
			newsletterType: !subscriptionTypes.length ? "general" : subscriptionTypes.join(","),
			...super.getCommonEventParameters(),
		};
	}

	protected getComponentName(): string {
		return "NewsletterSubscribe";
	}
}

export default NewsletterSubscribe;
